import * as React from 'react';
import { useMemo, useCallback, useRef, useState } from 'react';
import { trpc } from '@org/clients/school';
import { useAuth, unwrapTrpcData, DynamicForm, MultipleSelector, TextArea, Button, DateTimeField, Alert, AlertDescription } from '@org/ui';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@org/ui';
import type { Option } from '@org/ui';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';
import type { ControllerRenderProps, UseFormReturn } from 'react-hook-form';

import { EventDeleteConfirmDialog } from './event-delete-confirm-dialog';
import { RecurrenceControls } from './recurrence-controls';
import {
  type EventSummary,
  type EventFormValues,
  eventFormSchema,
  formatParticipantName,
  buildOptions,
  resolveDefaultOptions,
  isValidUuid,
  buildEventFormDefaultValues,
  alignEndDateWithStart,
  getLockedDateFromStart,
} from './event-form.helpers';
import {
  combineDefaultTimes,
  toDateTimeInput,
  parseDateTimeInput,
  ensureValidDuration,
  translateWeekdaysToUTC,
  type WeekdayCode,
} from '@org/utils-client';

// =============================================================================
// Types
// =============================================================================

type EventFormMode = 'create' | 'edit';

type EventFormProps = {
  mode: EventFormMode;
  defaultEvent?: EventSummary | null;
  anchorDate?: Date;
  onSuccess: () => void;
  onClose: () => void;
};

// =============================================================================
// Helpers
// =============================================================================

const buildAssociationPayload = (values: EventFormValues) => ({
  groups: values.groupIds.map(opt => ({ groupId: opt.value, accessType: 'exclusive' as const })),
  classes: values.classIds.map(opt => ({ classId: opt.value })),
  attendees: values.attendeeIds.map(opt => ({ userId: opt.value })),
});

const buildBasePayload = (values: EventFormValues, startDate: Date, endDate: Date, createdByUserId: string) => ({
  title: values.title,
  description: values.description,
  eventKind: values.eventKind,
  eventCategoryId: values.eventCategoryId,
  startTime: startDate,
  endTime: endDate,
  location: values.location,
  hostUserId: values.hostUserId,
  createdByUserId,
});

const buildRecurrencePayload = (values: EventFormValues, startDate: Date) => ({
  frequency: values.recurrenceFrequency,
  weekdays: translateWeekdaysToUTC(values.recurrenceWeekdays as WeekdayCode[], startDate),
  endDate: values.recurrenceEndDate ?? null,
});

// =============================================================================
// Sub-components
// =============================================================================

type EndDateTimeRendererProps = {
  field: ControllerRenderProps<EventFormValues, 'endDateTime'>;
  methods: UseFormReturn<EventFormValues>;
};

function EndDateTimeRenderer({ field, methods }: EndDateTimeRendererProps) {
  const { t } = useTranslation('admin');
  const startValue = methods.watch('startDateTime');
  const currentValue = field.value as string | undefined;
  const lockedDate = useMemo(() => getLockedDateFromStart(startValue), [startValue]);

  React.useEffect(() => {
    const aligned = alignEndDateWithStart(startValue, currentValue, toDateTimeInput);
    if (aligned && aligned !== currentValue) {
      field.onChange(aligned);
    }
  }, [startValue, currentValue, field]);

  const handleChange = useCallback(
    (nextValue?: string) => {
      if (!nextValue) {
        field.onChange(nextValue);
        return;
      }
      field.onChange(alignEndDateWithStart(startValue, nextValue, toDateTimeInput));
    },
    [field, startValue],
  );

  return (
    <DateTimeField
      label={t('events.form.labels.endDateTime')}
      value={currentValue}
      onChange={handleChange}
      placeholder={t('events.form.placeholders.endDate')}
      dateDisabled
      lockedDate={lockedDate}
    />
  );
}

const createMultiSelectRenderer = (options: Option[], placeholderKey: string) =>
  function MultiSelectField({ field }: { field: ControllerRenderProps<EventFormValues> }) {
    const { t } = useTranslation('admin');
    return (
      <MultipleSelector
        value={(field.value as Option[] | undefined) ?? []}
        options={options}
        defaultOptions={options}
        onChange={field.onChange}
        placeholder={t(placeholderKey)}
      />
    );
  };

const PRIVATE_CATEGORY_SLUG = 'private';
// Radix Select items cannot have an empty-string value, so "no journey" uses a sentinel.
const JOURNEY_NONE_VALUE = '__none__';

const CLASS_EVENT_KINDS = new Set(['group-class', 'private-class', 'tutoring']);

// =============================================================================
// Main Component
// =============================================================================

export function EventForm({ mode, defaultEvent, anchorDate, onSuccess, onClose }: EventFormProps) {
  const utils = trpc.useUtils();
  const { user } = useAuth();
  const { t } = useTranslation('admin');
  const formRef = useRef<HTMLFormElement>(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  // Data queries
  const { data: categoriesData } = trpc.eventCategories.list.useQuery();
  const { data: groupsData } = trpc.groups.list.useQuery();
  const { data: classesData } = trpc.classes.list.useQuery();
  const { data: usersData } = trpc.users.list.useQuery();
  const { data: journeysData } = trpc.journeys.getJourneys.useQuery();

  // Unwrap data
  const categories = unwrapTrpcData(categoriesData) ?? [];
  const rawGroups = unwrapTrpcData(groupsData);
  const groups = useMemo(() => (rawGroups ?? []).filter((g: any) => g?.isActive), [rawGroups]);
  const classes = unwrapTrpcData(classesData) ?? [];
  const rawUsers = unwrapTrpcData(usersData);
  const users = useMemo(() => (rawUsers ?? []).filter((u: any) => u?.enabled), [rawUsers]);
  const journeys = unwrapTrpcData(journeysData) ?? [];

  // Build options
  const categoryOptions = useMemo(() =>
    buildOptions(categories, (c: any) => c.eventCategoryId, (c: any) => c.name ?? t('events.form.misc.uncategorized'), { extend: (c: any) => ({ slug: c.slug as string }) }),
    [categories, t]
  );

  const privateCategoryId = useMemo(() =>
    categoryOptions.find(c => (c as any).slug === PRIVATE_CATEGORY_SLUG)?.value,
    [categoryOptions]
  );


  const hostOptions = useMemo(() =>
    buildOptions(users, (u: any) => u.userId, formatParticipantName, { filter: (u: any) => u.isTeacher }),
    [users]
  );

  const groupOptions = useMemo(() =>
    buildOptions(groups, (g: any) => g.groupId, (g: any) => g.description ?? t('events.form.misc.unnamedGroup')),
    [groups, t]
  );

  const classOptions = useMemo(() =>
    buildOptions(classes, (c: any) => c.classId, (c: any) => c.name ?? t('events.form.misc.unnamedClass')),
    [classes, t]
  );

  const attendeeOptions = useMemo(() =>
    buildOptions(users, (u: any) => u.userId, formatParticipantName),
    [users]
  );

  const journeyOptions = useMemo(
    () =>
      buildOptions(
        journeys,
        (j: any) => j.journeyId,
        (j: any) => j.name ?? 'Unnamed Journey',
      ),
    [journeys],
  );

  // Form configuration
  const initialEvent = defaultEvent ?? null;
  const isSeriesEvent = Boolean(initialEvent?.seriesTemplateId || initialEvent?.isTemplate);
  const scopedEditingAvailable = mode === 'edit' && isSeriesEvent;

  const defaultTimes = useMemo(() => combineDefaultTimes(anchorDate), [anchorDate]);

  const defaultSelections = useMemo(() => ({
    groups: resolveDefaultOptions(initialEvent?.groups, groupOptions, g => g.groupId, g => g.description ?? t('events.form.misc.groupLabel')),
    classes: resolveDefaultOptions(initialEvent?.classes, classOptions, c => c.classId, c => c.name ?? t('events.form.misc.classLabel')),
    attendees: resolveDefaultOptions(initialEvent?.attendees, attendeeOptions, a => a.userId, formatParticipantName),
  }), [initialEvent, groupOptions, classOptions, attendeeOptions, t]);

  const defaultValues = useMemo(() => {
    const values = buildEventFormDefaultValues({
      initialEvent,
      categoryOptions,
      hostOptions,
      defaultStartIso: toDateTimeInput(defaultTimes.start),
      defaultEndIso: toDateTimeInput(defaultTimes.end),
      defaultGroupOptions: defaultSelections.groups,
      defaultClassOptions: defaultSelections.classes,
      defaultAttendeeOptions: defaultSelections.attendees,
      scopedEditingAvailable,
      toDateTimeInput,
    });
    // Auto-set category based on event kind when no initial event
    if (!initialEvent && privateCategoryId && CLASS_EVENT_KINDS.has(values.eventKind)) {
      values.eventCategoryId = privateCategoryId;
    }
    return values;
  },
    [initialEvent, categoryOptions, hostOptions, defaultTimes, defaultSelections, scopedEditingAvailable, privateCategoryId]
  );

  // Journey class suggestion (create mode only)
  const [watchedGroupIds, setWatchedGroupIds] = useState<Option[]>([]);
  const firstGroupId = useMemo(() => {
    const first = watchedGroupIds?.[0];
    return first?.value ?? null;
  }, [watchedGroupIds]);

  const { data: classSuggestion } = trpc.journeys.getNextClassForGroup.useQuery(
    { groupId: firstGroupId! },
    { enabled: mode === 'create' && !!firstGroupId }
  );

  // Mutations
  const invalidateEvents = useCallback(() => utils.events.list.invalidate(), [utils]);

  const createSingleMutation = trpc.events.createSingle.useMutation({ onSuccess: invalidateEvents });
  const createSeriesMutation = trpc.events.createSeries.useMutation({ onSuccess: invalidateEvents });
  const updateMutation = trpc.events.update.useMutation({
    onSuccess: (_, vars) => { invalidateEvents(); if (vars) utils.events.get.invalidate(vars.eventId); },
  });
  const updateSeriesMutation = trpc.events.updateSeries.useMutation({
    onSuccess: (_, vars) => { invalidateEvents(); if (vars) utils.events.get.invalidate(vars.eventId); },
  });
  const promoteToSeriesMutation = trpc.events.promoteToSeries.useMutation({
    onSuccess: (_, vars) => { invalidateEvents(); if (vars) utils.events.get.invalidate(vars.eventId); },
  });
  const deleteMutation = trpc.events.delete.useMutation({
    onSuccess: (_, vars) => { invalidateEvents(); if (vars) utils.events.get.invalidate(vars.eventId); },
  });

  const isSubmitting = createSingleMutation.isPending || createSeriesMutation.isPending ||
    updateMutation.isPending || updateSeriesMutation.isPending || promoteToSeriesMutation.isPending;

  // Submit handler
  const handleSubmit = async (values: EventFormValues) => {
    try {
      const startDate = parseDateTimeInput(values.startDateTime);
      const endDate = parseDateTimeInput(values.endDateTime);
      ensureValidDuration(startDate, endDate);

      const associations = buildAssociationPayload(values);
      const createdByUserId = isValidUuid(user?.userId) ? (user?.userId as string) : values.hostUserId;
      const basePayload = buildBasePayload(values, startDate, endDate, createdByUserId);

      if (mode === 'create') {
        if (values.isRecurring) {
          const seriesInput: {
            template: typeof basePayload & { status: 'scheduled' };
            recurrence: ReturnType<typeof buildRecurrencePayload>;
            groups: ReturnType<typeof buildAssociationPayload>['groups'];
            classes: ReturnType<typeof buildAssociationPayload>['classes'];
            attendees: ReturnType<typeof buildAssociationPayload>['attendees'];
            journeyId?: string;
          } = {
            template: { ...basePayload, status: 'scheduled' as const },
            recurrence: buildRecurrencePayload(values, startDate),
            ...associations,
          };
          if (values.journeyId) {
            seriesInput.journeyId = values.journeyId;
          }
          const result = await createSeriesMutation.mutateAsync(seriesInput);
          toast.success(t('common.toasts.createSuccess', { entity: 'Recurring events' }));
          const unattached = (result as any)?.unattachedClassIds as string[] | undefined;
          if (unattached && unattached.length > 0) {
            toast.warning(
              t('events.form.toasts.journeyTruncated', { count: unattached.length }),
            );
          }
        } else {
          await createSingleMutation.mutateAsync({ ...basePayload, status: 'scheduled' as const, ...associations });
          toast.success(t('common.toasts.createSuccess', { entity: 'Event' }));
        }
      } else {
        if (!initialEvent) {
          toast.error(t('events.toasts.loadError'));
          return;
        }

        const scope = values.applyScope ?? (initialEvent.isTemplate ? 'series' : 'occurrence');

        if (scope === 'series' && isSeriesEvent) {
          const targetEventId = initialEvent.isTemplate ? initialEvent.eventId : (initialEvent.seriesTemplateId as string);
          await updateSeriesMutation.mutateAsync({
            eventId: targetEventId,
            data: basePayload,
            ...associations,
            applyToFutureOccurrences: true,
            forceResetAllAssociations: values.forceResetAllAssociations,
            ...(values.isRecurring && { recurrence: buildRecurrencePayload(values, startDate) }),
          });
          toast.success(t('common.toasts.updateSuccess', { entity: 'Series' }));
        } else if (!isSeriesEvent && values.isRecurring) {
          // Standalone event being made recurring. events.update silently drops
          // recurrence, so route to the promotion endpoint, which keeps this
          // event as the first occurrence and builds the series around it.
          await promoteToSeriesMutation.mutateAsync({
            eventId: initialEvent.eventId,
            data: basePayload,
            recurrence: buildRecurrencePayload(values, startDate),
            ...associations,
          });
          toast.success(t('common.toasts.updateSuccess', { entity: 'Series' }));
        } else {
          await updateMutation.mutateAsync({ eventId: initialEvent.eventId, data: basePayload, ...associations });
          toast.success(t('common.toasts.updateSuccess', { entity: 'Event' }));
        }
      }

      onSuccess();
    } catch (error) {
      toast.error(error instanceof Error ? error.message : t('common.feedback.genericError'));
      throw error instanceof Error ? error : new Error('Failed to save event');
    }
  };

  // Delete handlers
  const handleDeleteClick = useCallback(() => setShowDeleteConfirm(true), []);

  const handleConfirmDelete = useCallback(async (deleteSeries: boolean) => {
    if (!initialEvent) return;

    const targetEventId = deleteSeries
      ? (initialEvent.isTemplate ? initialEvent.eventId : (initialEvent.seriesTemplateId as string))
      : initialEvent.eventId;

    setShowDeleteConfirm(false);

    try {
      await deleteMutation.mutateAsync({ eventId: targetEventId, deleteFutureOccurrences: deleteSeries });
      toast.success(deleteSeries ? t('common.toasts.deleteSuccess', { entity: 'Series' }) : t('common.toasts.deleteSuccess', { entity: 'Event' }));
      invalidateEvents();
      utils.events.get.invalidate(initialEvent.eventId);
      if (initialEvent.seriesTemplateId) {
        utils.events.get.invalidate(initialEvent.seriesTemplateId);
      }
      onSuccess();
      onClose();
    } catch (error) {
      console.error('Failed to delete event', error);
      toast.error(t('common.toasts.actionError', { action: 'delete', entity: 'event' }));
    }
  }, [deleteMutation, initialEvent, onClose, onSuccess, invalidateEvents, utils, t]);

  // Form configuration
  const labelMappings: Record<string, string> = useMemo(() => ({
    title: t('events.form.labels.title'),
    description: t('events.form.labels.description'),
    eventKind: t('events.form.labels.eventKind'),
    eventCategoryId: t('events.form.labels.category'),
    hostUserId: t('events.form.labels.host'),
    location: t('events.form.labels.location'),
    startDateTime: t('events.form.labels.startDateTime'),
    endDateTime: t('events.form.labels.endDateTime'),
    groupIds: t('events.form.labels.groups'),
    classIds: t('events.form.labels.classes'),
    attendeeIds: t('events.form.labels.attendees'),
    isRecurring: t('events.form.labels.isRecurring'),
    recurrenceFrequency: t('events.form.labels.frequency'),
    applyScope: t('events.form.labels.applyScope'),
    journeyId: t('events.form.labels.journeyId'),
  }), [t]);

  const foreignKeyOptions = useMemo(() => ({
    eventCategories: categoryOptions,
    hostUsers: hostOptions,
  }), [categoryOptions, hostOptions]);

  const customRenderers = useMemo(() => ({
    journeyId: ({ field, methods }: any) => {
      const isRecurring = methods.watch('isRecurring') as boolean;
      if (mode !== 'create' || !isRecurring) return null;
      const currentJourneyId = (field.value ?? '') as string;
      const selectedJourney = journeys.find((j: any) => j.journeyId === currentJourneyId);
      const selectedJourneyName = selectedJourney?.name ?? '';
      return (
        <div className="space-y-2">
          <Select
            value={currentJourneyId || JOURNEY_NONE_VALUE}
            onValueChange={(value) => field.onChange(value === JOURNEY_NONE_VALUE ? '' : value)}
          >
            <SelectTrigger className="w-full">
              <SelectValue placeholder={t('events.form.misc.journeyNone')} />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={JOURNEY_NONE_VALUE}>{t('events.form.misc.journeyNone')}</SelectItem>
              {journeyOptions.map((opt) => (
                <SelectItem key={opt.value} value={opt.value}>
                  {opt.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {currentJourneyId && (
            <Alert>
              <AlertDescription className="text-sm text-muted-foreground">
                {t('events.form.preview.journeySpread', { journey: selectedJourneyName })}
              </AlertDescription>
            </Alert>
          )}
        </div>
      );
    },
    description: ({ field }: any) => (
      <TextArea
        value={(field.value ?? '') as string}
        onChange={field.onChange}
        className="w-full p-2 border rounded focus:ring-2 focus:ring-blue-500"
        rows={3}
        placeholder={t('events.form.placeholders.description')}
      />
    ),
    eventKind: ({ field, methods }: any) => {
      const handleKindChange = (newKind: string) => {
        field.onChange(newKind);
        if (CLASS_EVENT_KINDS.has(newKind) && privateCategoryId) {
          methods.setValue('eventCategoryId', privateCategoryId);
        } else if (!CLASS_EVENT_KINDS.has(newKind)) {
          const currentCatId = methods.getValues('eventCategoryId');
          if (currentCatId === privateCategoryId) {
            const firstNonPrivate = categoryOptions.find(c => (c as any).slug !== PRIVATE_CATEGORY_SLUG);
            methods.setValue('eventCategoryId', firstNonPrivate?.value ?? '');
          }
        }
      };
      return (
        <Select value={(field.value ?? 'group-class') as string} onValueChange={handleKindChange}>
          <SelectTrigger className="w-full">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="group-class">{t('events.form.options.eventTypes.groupClass')}</SelectItem>
            <SelectItem value="private-class">{t('events.form.options.eventTypes.privateClass')}</SelectItem>
            <SelectItem value="closed-event">{t('events.form.options.eventTypes.closedEvent')}</SelectItem>
            <SelectItem value="open-event">{t('events.form.options.eventTypes.openEvent')}</SelectItem>
            <SelectItem value="tutoring">{t('events.form.options.eventTypes.tutoring')}</SelectItem>
          </SelectContent>
        </Select>
      );
    },
    eventCategoryId: ({ field, methods }: any) => {
      const eventKind = methods.watch('eventKind') as string;
      const isClassType = CLASS_EVENT_KINDS.has(eventKind);
      const filteredOptions = isClassType
        ? categoryOptions.filter(c => (c as any).slug === PRIVATE_CATEGORY_SLUG)
        : categoryOptions.filter(c => (c as any).slug !== PRIVATE_CATEGORY_SLUG);
      return (
        <Select
          value={((field.value ?? '') as string) || undefined}
          onValueChange={value => field.onChange(value)}
          disabled={isClassType}
        >
          <SelectTrigger className="w-full">
            <SelectValue placeholder={t('events.form.placeholders.category', 'Select a category')} />
          </SelectTrigger>
          <SelectContent>
            {filteredOptions.map(opt => (
              <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      );
    },
    startDateTime: ({ field }: any) => (
      <DateTimeField
        label={t('events.form.labels.startDateTime')}
        value={field.value as string | undefined}
        onChange={field.onChange}
        placeholder={t('events.form.placeholders.startDate')}
      />
    ),
    endDateTime: ({ field, methods }: any) => (
      <EndDateTimeRenderer field={field} methods={methods} />
    ),
    isRecurring: ({ methods }: any) => (
      <RecurrenceControls methods={methods} mode={mode} scopedEditingAvailable={scopedEditingAvailable} />
    ),
    groupIds: ({ field }: { field: ControllerRenderProps<EventFormValues> }) => {
      // Track group selection for journey suggestion
      const currentValue = (field.value as Option[] | undefined) ?? [];
      React.useEffect(() => {
        setWatchedGroupIds(currentValue);
      }, [currentValue]);
      return (
        <MultipleSelector
          value={currentValue}
          options={groupOptions}
          defaultOptions={groupOptions}
          onChange={field.onChange}
          placeholder={t('events.form.placeholders.groups')}
        />
      );
    },
    classIds: ({ field, methods }: { field: ControllerRenderProps<EventFormValues>; methods: UseFormReturn<EventFormValues> }) => {
      const currentValue = (field.value as Option[] | undefined) ?? [];
      const journeyIdValue = methods.watch('journeyId') as string | undefined;
      const isRecurring = methods.watch('isRecurring') as boolean;
      // Journey spread takes precedence over per-group class suggestion banner.
      const journeyActive = Boolean(journeyIdValue && isRecurring);
      const handleUseSuggestion = () => {
        if (!classSuggestion?.suggestion) return;
        const { classId, className } = classSuggestion.suggestion;
        // Don't add if already selected
        if (currentValue.some((opt) => opt.value === classId)) return;
        const newOption: Option = { value: classId, label: className ?? 'Unnamed Class' };
        field.onChange([...currentValue, newOption]);
      };
      return (
        <div className="space-y-2">
          {mode === 'create' && !journeyActive && classSuggestion?.suggestion && (
            <Alert>
              <AlertDescription className="flex items-center justify-between gap-2">
                <div className="flex-1">
                  <p className="text-sm font-medium">
                    Journey suggests: {classSuggestion.suggestion.className} (from {classSuggestion.suggestion.unitName})
                  </p>
                  <p className="text-xs text-muted-foreground">
                    {classSuggestion.assignedClasses}/{classSuggestion.totalClasses} classes assigned from {classSuggestion.journeyName}
                  </p>
                </div>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={handleUseSuggestion}
                  disabled={currentValue.some((opt) => opt.value === classSuggestion.suggestion!.classId)}
                >
                  {currentValue.some((opt) => opt.value === classSuggestion.suggestion!.classId) ? 'Added' : 'Use Suggestion'}
                </Button>
              </AlertDescription>
            </Alert>
          )}
          {mode === 'create' && !journeyActive && classSuggestion?.reason === 'all_assigned' && classSuggestion.journeyName && (
            <Alert>
              <AlertDescription className="text-sm text-muted-foreground">
                All {classSuggestion.totalClasses} classes from {classSuggestion.journeyName} are already assigned to events.
              </AlertDescription>
            </Alert>
          )}
          <MultipleSelector
            value={currentValue}
            options={classOptions}
            defaultOptions={classOptions}
            onChange={field.onChange}
            placeholder={t('events.form.placeholders.classes')}
          />
        </div>
      );
    },
    attendeeIds: createMultiSelectRenderer(attendeeOptions, 'events.form.placeholders.attendees'),
  }), [t, mode, scopedEditingAvailable, groupOptions, classOptions, attendeeOptions, initialEvent, categoryOptions, privateCategoryId, journeyOptions, journeys, classSuggestion]);

  const hiddenFields = ['recurrenceWeekdays', 'recurrenceEndDate', 'recurrenceFrequency', 'applyScope', 'forceResetAllAssociations'];

  const fieldVisibility = useCallback(({ key, values }: { key: string; values?: any }) => {
    if (key === 'applyScope') return scopedEditingAvailable;
    if (key === 'journeyId') {
      // Journey spread is only available when creating a recurring series.
      return mode === 'create' && Boolean(values?.isRecurring);
    }
    return true;
  }, [scopedEditingAvailable, mode]);

  // Render
  const deleteDisabled = isSubmitting || deleteMutation.isPending || !initialEvent;
  const actionDisabled = deleteMutation.isPending;
  const actionLabel = mode === 'edit' ? t('common.actions.update') : t('common.actions.create');

  return (
    <>
      <div className="space-y-4">
        <DynamicForm
          schema={eventFormSchema}
          onSubmit={handleSubmit}
          onClose={onClose}
          defaultValues={defaultValues}
          isEditing={mode === 'edit'}
          hiddenFields={hiddenFields}
          labelMappings={labelMappings}
          foreignKeyOptions={foreignKeyOptions}
          customRenderers={customRenderers as Record<string, any>}
          fieldVisibility={fieldVisibility as any}
          hideButtons
          formRef={formRef}
        />

        <div className="flex flex-wrap items-center justify-between gap-3">
          {mode === 'edit' && (
            <Button variant="destructive" onClick={handleDeleteClick} disabled={deleteDisabled}>
              {t('events.form.actions.delete')}
            </Button>
          )}
          <div className="ml-auto flex gap-2">
            <Button variant="outline" onClick={onClose} disabled={isSubmitting || deleteMutation.isPending}>
              {t('common.actions.cancel')}
            </Button>
            <Button
              onClick={() => formRef.current?.requestSubmit()}
              isLoading={isSubmitting}
              loadingText={mode === 'edit' ? t('common.status.updating') : t('common.status.creating')}
              disabled={actionDisabled}
            >
              {actionLabel}
            </Button>
          </div>
        </div>
      </div>

      {initialEvent && (
        <EventDeleteConfirmDialog
          open={showDeleteConfirm}
          onOpenChange={setShowDeleteConfirm}
          eventTitle={initialEvent.title}
          isSeriesEvent={isSeriesEvent}
          onConfirmDelete={handleConfirmDelete}
          isDeleting={deleteMutation.isPending}
        />
      )}
    </>
  );
}
